import React, { useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  SET_PLAN_PERIOD_TYPE,
  SET_USER_AND_PLAN_DETAILS,
  SET_IS_LOADING,
} from "../../../actions/types";
import IdeaIcon from "../../../assets/icons/idea_icon.svg";
import {
  Row,
  Col
} from "reactstrap";
import ToolTip from "../../events/Components/Common/ToolTip";
import ConfirmPaymentModal from "./confirm-payment-modal";
import CancelPlanModal from "./cancel-plan-modal";
import PlanDetailModal from "./plan-detail-modal";
import isEmpty from "../../../validation/is-empty";
import CardInfoModal from "./card-info-modal";
import { notification } from "../../../utils/utility";
import axiosInstance from "../../../axios-instance";
import Loader from "../../../components/Loader/Loader";
import UpdateCardModal from "./update-card-modal";

const SubscriptionPlans = (props) => {
  const dispatch = useDispatch();

  const [showCardInfoModal, setShowCardInfoModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSentence, setLoadingSentence] = useState(null);
  const [showChangeCardModal, setShowChangeCardModal] = useState(false);
  // per-row individual/team override (keyed by row index); empty = follow user's plan type
  const [rowVariantOverride, setRowVariantOverride] = useState({});
  const { plans, planPeriodType, userPlan } = props.subscription;

  /**
   * toggle a single card between its individual and team variant
   * @param int rowIndex
   * @param string currentVariant
   */
  const toggleRowVariant = (rowIndex, currentVariant) => {
    setRowVariantOverride((prev) => ({
      ...prev,
      [rowIndex]: currentVariant === "team" ? "individual" : "team",
    }));
  };

  /**
   * on cancel plan
   * @param {*} reason
   * @param {*} natureOfIssue
   * @param {*} preferredContact
   */
  const onCancelSubscription = async (reason, natureOfIssue, preferredContact) => {
    const { getUserAndPlans } = props;

    dispatch({ type: SET_IS_LOADING, payload: true });

    let res;
    try {
      res = await axiosInstance.post("/admin/subscription/cancel", {});
    } catch (err) {
      dispatch({ type: SET_IS_LOADING, payload: false });
      notification.error(
        err?.response?.data?.message || "An error occurred while cancelling. Please try again."
      );
      return;
    }

    dispatch({ type: SET_IS_LOADING, payload: false });

    if (res.data.success) {
      notification.success(res.data.message);
      setLoadingSentence("Please wait, your subscription is being cancelled. This may take a few moments.");
      setIsLoading(true);
      setTimeout(() => {
        getUserAndPlans();
        setIsLoading(false);
      }, 15000);
    } else {
      notification.error(res.data.message || "Cancellation failed. Please try again.");
    }
  };

  /**
   * upgrade plan handler
   * @param {*} data
   */
  const upgradePlanHandler = async (data) => {
    const { getUserAndPlans } = props;
    const { selectedPlan, planPeriodType } = props.subscription;

    dispatch({ type: SET_IS_LOADING, payload: true });

    let res;
    try {
      res = await axiosInstance.post("/admin/subscription/update", {
        plan_id:   selectedPlan.id,
        plan_type: planPeriodType,
      });
    } catch (err) {
      dispatch({ type: SET_IS_LOADING, payload: false });
      notification.error(
        err?.response?.data?.message || "An error occurred while updating your plan. Please try again."
      );
      return;
    }

    dispatch({ type: SET_IS_LOADING, payload: false });

    if (res.data.success) {
      notification.success(res.data.message);
      setLoadingSentence("Please wait, your subscription is being updated. This may take a few moments.");
      setIsLoading(true);
      setTimeout(() => {
        getUserAndPlans();
        setIsLoading(false);
      }, 15000);
    } else {
      notification.error(res.data.message || "Plan update failed. Please try again.");
    }
  };

  /**
   * on upgrade plan, in case you are moving from free plan to paid
   * @param {*} plan
   */
  const onUpgradePlan = (plan) => {
    setShowCardInfoModal(true);
    dispatch({
      type: SET_USER_AND_PLAN_DETAILS,
      payload: {
        selectedPlan: plan,
      },
    });
  };

  const isTeamPlan = userPlan?.plan_type === "team";

  // group active plans by type (deactivated plans are already filtered out by the API)
  const individualPlans = plans.filter((p) => p.plan_type === "individual");
  const teamPlans = plans.filter((p) => p.plan_type === "team");
  const rowCount = Math.max(individualPlans.length, teamPlans.length);

  // resolve the plan to display for each card row, honoring per-row toggle overrides
  const displayRows = Array.from({ length: rowCount })
    .map((_, rowIndex) => {
      const variant = rowVariantOverride[rowIndex] || (isTeamPlan ? "team" : "individual");
      const preferred = variant === "team" ? teamPlans[rowIndex] : individualPlans[rowIndex];
      const plan = preferred || teamPlans[rowIndex] || individualPlans[rowIndex];
      return plan ? { plan, rowIndex } : null;
    })
    .filter(Boolean);

  return (
    <>
      {/* Monthly Anullay Switch */}
      <Loader isShowLoader={isLoading} loadingText={loadingSentence} />
      {showChangeCardModal && <UpdateCardModal toggle={setShowChangeCardModal}/>}
      <div className="mb-4 d-flex flex-wrap justify-content-center align-items-center gap-3">
        <div className="billing-period-toggle" role="group" aria-label="Billing period">
          <button
            type="button"
            className={`billing-period-toggle__btn ${
              props.subscription.planPeriodType === "monthly" ? "active" : ""
            }`}
            onClick={() =>
              dispatch({ type: SET_PLAN_PERIOD_TYPE, payload: "monthly" })
            }
          >
            Monthly
          </button>
          <button
            type="button"
            className={`billing-period-toggle__btn ${
              props.subscription.planPeriodType === "annual" ? "active" : ""
            }`}
            onClick={() =>
              dispatch({ type: SET_PLAN_PERIOD_TYPE, payload: "annual" })
            }
          >
            Annually
          </button>
        </div>
        {userPlan.is_paid_subscription === "E" && (
          <button
            type="button"
            className="btn btn_success btn-14"
            style={{ width: "auto" }}
            onClick={() => setShowChangeCardModal(true)}
          >
            Change Card
          </button>
        )}
        {userPlan.is_paid_subscription === "E" && (
          <CancelPlanModal cancelPlan={onCancelSubscription} />
        )}
      </div>
      {/* Annual Subscription Benefit */}
      <div className="cmplt_prof_msg subscription_msg">
        <img src={IdeaIcon} alt="Alert Icon" />{" "}
        <p>
          You Can Save <span>$50</span> On Annually Subscription
        </p>
      </div>
      <Row className="pt-2 g-4">
        {showCardInfoModal && (
          <CardInfoModal
            getUserAndPlans={props.getUserAndPlans}
            setShowCardInfoModal={setShowCardInfoModal}
            selectedPlan={props.subscription.selectedPlan}
            planPeriodType={props.subscription.planPeriodType}
          />
        )}
        {displayRows
          .map(({ plan, rowIndex }) => {
            return (
              <Col lg="4" md="6" key={plan.id}>
                <div className="subscription_card">
                  <div className="cd_head">
                    <h3>
                      {plan.plan_name}{" "}
                      <label>
                        {planPeriodType === "monthly" ? (
                          <>(Monthly)</>
                        ) : (
                          <>(Annually)</>
                        )}
                      </label>
                      <ToolTip />
                    </h3>
                    <div className="w-auto d-flex justify-content-center align-items-center">
                      <label className="form-check-label mb-0">
                        Individual
                      </label>
                      <div className="toggle_field mx-2">
                        <div className="box_content bg-transparent p-0">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={plan.plan_type === "team"}
                              onChange={() =>
                                toggleRowVariant(rowIndex, plan.plan_type)
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <label className="form-check-label mb-0">Organizations</label>
                    </div>
                  </div>
                  <div className="cd_body">
                    <p className="subscription-card__desc">
                      Start automating basic scheduling features
                    </p>
                    <h2 className="subscription-card__price">
                      $
                      {planPeriodType === "monthly"
                        ? plan.monthly_price
                        : plan.annual_price}
                      .00
                      {plan.plan_type === "team" && <small>/ user</small>}
                    </h2>

                    <div className="subscription-card__actions">
                      <PlanDetailModal
                        plans={plans}
                        userPlan={userPlan}
                        planPeriodType={planPeriodType}
                        onBuyPlan={onUpgradePlan}
                      />
                      {plan.id === userPlan.plan_id ? (
                        <button className="btn btn_success">Active</button>
                      ) : (
                        <>
                          {/* Already paid subscription */}
                          {userPlan.is_paid_subscription === "E" ? (
                            <>
                              <ConfirmPaymentModal
                                price={
                                  planPeriodType === "monthly"
                                    ? plan.monthly_price
                                    : plan.annual_price
                                }
                                plan_id={plan.id}
                                plan={plan}
                                planPeriodType={planPeriodType}
                                userCurrentPlan={userPlan}
                                upgradePlan={upgradePlanHandler}
                              />
                            </>
                          ) : (
                            <>
                              {/* Go to Payment Page */}
                              {
                                ((planPeriodType === "monthly" && plan.monthly_price > userPlan.monthly_price) ||
                                (planPeriodType === "annual" && plan.annual_price > userPlan.annual_price) ||
                                  isEmpty(userPlan.plan_id)) ? (
                                <button
                                  className="btn btn-outline brand"
                                  onClick={() => onUpgradePlan(plan)}
                                >
                                  Upgrade Plan
                                </button>
                              ) : (
                                <button 
                                className="btn btn-outline brand"
                                onClick={() => onUpgradePlan(plan)}
                                >
                                  Downgrade Plan
                                </button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
      </Row>
    </>
  );
};

const mapStateToProps = (state) => ({
  subscription: state.subscription,
});

export default connect(mapStateToProps, null)(SubscriptionPlans);
